import {colors} from 'styles/colors';
import {IPermissionState, ITongPlaceState} from 'interfaces/interface';

/**
 * 통계 화면에서 사용하는 현장 상태 키
 * 실제 데이터 모델은 운영상태(state)와 승인상태(allowState)가 분리되어 있어,
 * 두 필드를 조합하여 백오피스 통계용 단일 상태 키로 환산합니다.
 */
export type ISiteStatusKey =
  | 'operating'
  | 'paused'
  | 'pendingApproval'
  | 'closed'
  | 'notStarted'
  | 'unknown';

/**
 * 현장 상태 키별 카운트 구조
 */
export interface ISiteStatusCounts {
  operating: number;
  paused: number;
  pendingApproval: number;
  closed: number;
  notStarted: number;
  unknown: number;
}

/**
 * 빈 현장 상태 카운트 객체를 생성합니다.
 * @returns {ISiteStatusCounts} 모든 값이 0인 카운트 객체
 */
export const createEmptySiteStatusCounts = (): ISiteStatusCounts => ({
  operating: 0,
  paused: 0,
  pendingApproval: 0,
  closed: 0,
  notStarted: 0,
  unknown: 0,
});

/**
 * 상태 키별 표시 정보 (한글 라벨 + 배지 색상)
 * 색상은 기존 styles/colors 토큰을 재사용합니다.
 */
export const siteStatusMeta: {
  [key in ISiteStatusKey]: {label: string; color: string};
} = {
  operating: {label: '운영중', color: colors.primary2},
  paused: {label: '중단', color: colors.darkGray},
  pendingApproval: {label: '승인대기', color: colors.darkLimeGreen},
  closed: {label: '종료', color: colors.gypsyRed},
  notStarted: {label: '준비중', color: colors.gray},
  unknown: {label: '알 수 없음', color: colors.borderGray},
};

/**
 * 현장의 운영상태(state)와 승인상태(allowState)를 통계용 단일 상태 키로 환산합니다.
 *
 * 매핑 기준 (승인대기 우선):
 * - allowState === 'REQUESTED'                 → pendingApproval (승인대기)
 * - state === 'IN_OPERATION'                   → operating (운영중)
 * - state === 'STOPPED'                        → paused (중단)
 * - state === 'COMPLETED'                      → closed (종료)
 * - state === 'NOT_STARTED'                    → notStarted (준비중)
 * - 그 외 / null / 알 수 없는 값               → unknown (알 수 없음)
 *
 * @param state - 현장 운영상태 (ITongPlaceState)
 * @param allowState - 현장 승인상태 (IPermissionState)
 * @returns {ISiteStatusKey} 통계용 상태 키
 */
export const getSiteStatusKey = (
  state?: ITongPlaceState | string | null,
  allowState?: IPermissionState | string | null,
): ISiteStatusKey => {
  if (allowState === 'REQUESTED') {
    return 'pendingApproval';
  }

  switch (state) {
    case 'IN_OPERATION':
      return 'operating';
    case 'STOPPED':
      return 'paused';
    case 'COMPLETED':
      return 'closed';
    case 'NOT_STARTED':
      return 'notStarted';
    default:
      return 'unknown';
  }
};

/**
 * 현장 상태의 한글 라벨을 반환합니다.
 * @param state - 현장 운영상태
 * @param allowState - 현장 승인상태
 * @returns {string} 한글 상태 라벨
 */
export const getSiteStatusLabel = (
  state?: ITongPlaceState | string | null,
  allowState?: IPermissionState | string | null,
): string => siteStatusMeta[getSiteStatusKey(state, allowState)].label;
